import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfigProvider } from "antd";
import { useRenewSessionMutation } from "./redux/api/authApi";
import { loadDebugSettings } from "./redux/slices/appSlice";
import { selectIsAuthenticated, selectToken, selectUser } from "./redux/slices/authSlice";
import PublicRoutes from "./routes/PublicRoutes";
import PrivateRoutes from "./routes/PrivateRoutes";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import theme from "./config/theme";

const App = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);
  const location = useLocation();
  const navigate = useNavigate();

  const [renewSession] = useRenewSessionMutation();

  // Check for existing token on app startup
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken && (!token || !user)) {
      // Try to renew the session if we have a token but no authenticated state or user data
      renewSession();
    }
  }, [renewSession, token, user]);

  // Load debug settings on app startup
  useEffect(() => {
    dispatch(loadDebugSettings());
  }, [dispatch]);

  // Redirect to dashboard if authenticated and on login page
  useEffect(() => {
    if (isAuthenticated && location.pathname === "/login") {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, location, navigate]);

  // Redirect to login if not authenticated and not on login page
  useEffect(() => {
    if (!isAuthenticated && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, location, navigate]);

  return (
    <ConfigProvider theme={theme}>
      {isAuthenticated ? <PrivateRoutes /> : <PublicRoutes />}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </ConfigProvider>
  );
};

export default App;
